import React, { useMemo, useState, useEffect, useCallback, isValidElement } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, X } from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getSortedRowModel,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

/**
 * ReusableTable
 *
 * Premium styled table component using @tanstack/react-table with:
 * - Dashboard-style premium UI (light-orange header, borders, hover effects)
 * - Sorting support
 * - Filtering support (min/max range filters)
 * - Column resizing
 * - Backward compatible with simple column definitions
 *
 * Props:
 * - columns: Array of column definitions (simple format or TanStack format)
 *   Simple format: { accessorKey, header, size?, minSize?, maxSize?, cell?, enableSorting?, enableFiltering? }
 *   TanStack format: Use createColumnHelper().accessor() format
 * - data: Array of row data
 * - enableSorting: Enable column sorting (default: true)
 * - enableFiltering: Enable column filtering (default: true)
 * - enableResizing: Enable column resizing (default: true)
 * - enablePagination: Enable pagination (default: false)
 * - onRowClick: Optional callback for row clicks
 * - emptyMessage: Custom message when no data (default: "No data available")
 * - initialState: Optional initial state (sorting, columnSizing, etc.)
 */
const ReusableTable = ({
  columns,
  data,
  enableSorting = true,
  enableFiltering = true,
  enableResizing = true,
  enablePagination: _enablePagination = false,
  onRowClick,
  emptyMessage = "No data available",
  initialState = {},
  // Legacy props for backward compatibility (ignored but accepted)
  grouping,
  setGrouping,
  columnVisibility,
  isLoading,
  ..._rest
}) => {
  const [sorting, setSorting] = useState(initialState.sorting || []);
  const [columnSizing, setColumnSizing] = useState(initialState.columnSizing || {});
  const [columnFilters, setColumnFilters] = useState(initialState.columnFilters || {});
  const [openFilterMenu, setOpenFilterMenu] = useState(null);

  // Convert simple column definitions to TanStack Table format
  const tableColumns = useMemo(() => {
    // Check if columns are already in TanStack format (have columnDef or are created with columnHelper)
    if (columns.length > 0 && (columns[0].id || columns[0].accessorFn || columns[0].columnDef)) {
      // Already in TanStack format, use as-is
      return columns;
    }

    // Convert simple format to TanStack format
    return columns.map((col) => {
      // Handle both 'cell' (TanStack) and 'Cell' (MaterialReactTable) for backward compatibility
      let cellRenderer = col.cell;
      
      if (!cellRenderer && col.Cell) {
        // Convert MaterialReactTable Cell format to TanStack cell format
        cellRenderer = (info) => {
          // Create a mock cell object for compatibility
          const mockCell = {
            getValue: () => info.getValue(),
            row: info.row,
          };
          const mockRow = {
            original: info.row.original,
          };
          
          // Call the original Cell function with MaterialReactTable format
          const result = col.Cell({ cell: mockCell, row: mockRow });
          
          // Wrap in div if not already wrapped
          if (typeof result === 'string' || typeof result === 'number') {
            return (
              <div className="text-sm text-gray-700" title={String(result)}>
                {result}
              </div>
            );
          }
          
          // If it's already a React element, wrap it to ensure truncation works
          return (
            <div className="min-w-0">
              {result}
            </div>
          );
        };
      }
      
      if (!cellRenderer) {
        // Default cell renderer with truncation
        cellRenderer = (info) => {
          const value = info.getValue();
          const displayValue = value !== undefined && value !== null ? String(value) : "-";
          return (
            <div className="text-sm text-gray-700" title={displayValue}>
              {displayValue}
            </div>
          );
        };
      } else {
        // Wrap existing cell renderer to ensure truncation for simple values
        const originalCell = cellRenderer;
        cellRenderer = (info) => {
          const result = originalCell(info);
          // If result is a string/number, wrap it with truncation
          if (typeof result === 'string' || typeof result === 'number') {
            return (
              <div className="text-sm text-gray-700" title={String(result)}>
                {result}
              </div>
            );
          }
          // For React elements, wrap in a container if needed
          if (isValidElement(result)) {
            const hasTruncate = result.props?.className?.includes('truncate');
            if (!hasTruncate && typeof result.props?.children === 'string') {
              return (
                <div className="min-w-0" title={result.props.children}>
                  {result}
                </div>
              );
            }
          }
          // Return as-is for complex components
          return result;
        };
      }

      const columnOptions = {
        header: col.header,
        size: col.size || 150,
        minSize: col.minSize || 100,
        maxSize: col.maxSize || 1000,
        enableResizing: enableResizing && col.enableResizing !== false,
        enableSorting: col.enableSorting !== false && enableSorting,
        enableColumnFilter: enableFiltering && col.enableFiltering !== false,
        cell: cellRenderer,
        ...(col.sortingFn ? { sortingFn: col.sortingFn } : {}),
        ...(col.sortDescFirst != null ? { sortDescFirst: col.sortDescFirst } : {}),
      };

      if (col.accessorFn) {
        return columnHelper.accessor(col.accessorFn, {
          id: col.id || col.accessorKey,
          ...columnOptions,
        });
      }

      return columnHelper.accessor(col.accessorKey, columnOptions);
    });
  }, [columns, enableResizing, enableSorting, enableFiltering]);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilterMenu && !event.target.closest('.filter-menu-container')) {
        setOpenFilterMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openFilterMenu]);

  // Format filter value for display
  const getFilterDisplayValue = useCallback((columnId) => {
    const filter = columnFilters[columnId];
    if (!filter) return null;
    const parts = [];
    if (filter.min !== undefined && filter.min !== null) {
      parts.push(`≥${filter.min}`);
    }
    if (filter.max !== undefined && filter.max !== null) {
      parts.push(`≤${filter.max}`);
    }
    return parts.length > 0 ? parts.join(' ') : null;
  }, [columnFilters]);

  // Filter data based on column filters
  const filteredData = useMemo(() => {
    if (!enableFiltering || Object.keys(columnFilters).length === 0) {
      return data || [];
    }

    return (data || []).filter((row) => {
      return Object.entries(columnFilters).every(([columnId, filter]) => {
        // Find the column by id
        const column = tableColumns.find(col => {
          // TanStack table columns have an id that matches the accessorKey
          return col.id === columnId || col.accessorKey === columnId;
        });
        
        if (!column) return true;

        // Get the accessor key - TanStack uses accessorKey or accessorFn
        const accessorKey = column.accessorKey || columnId;
        const value = accessorKey ? row[accessorKey] : undefined;
        
        // Try to parse as number
        const numValue = typeof value === 'number' ? value : parseFloat(value);

        // Skip non-numeric values for now (can be enhanced later)
        if (isNaN(numValue)) return true;

        if (filter.min !== undefined && filter.min !== null && numValue < filter.min) {
          return false;
        }
        if (filter.max !== undefined && filter.max !== null && numValue > filter.max) {
          return false;
        }
        return true;
      });
    });
  }, [data, columnFilters, enableFiltering, tableColumns]);

  // Create table with filtered data
  const tableWithFilteredData = useReactTable({
    data: filteredData,
    columns: tableColumns,
    state: {
      sorting,
      columnSizing,
    },
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: enableResizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    initialState: {
      columnSizing: initialState.columnSizing || {},
      sorting: initialState.sorting || [],
    },
  });

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-full">
      <div className="overflow-x-auto w-full">
        <table style={{ tableLayout: 'fixed', width: tableWithFilteredData.getCenterTotalSize(), minWidth: '100%' }}>
          <thead className="bg-light-orange sticky top-0 z-10">
            {tableWithFilteredData.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const canFilter =
                    enableFiltering &&
                    header.column.id !== "name" &&
                    (typeof header.column.getCanFilter === "function"
                      ? header.column.getCanFilter()
                      : false);
                  const canSort = enableSorting && header.column.getCanSort();
                  const minSize = header.column.columnDef.minSize ?? 100;
                  const size = header.getSize();

                  return (
                    <th
                      key={header.id}
                      style={{ width: size, minWidth: minSize, maxWidth: header.column.columnDef.maxSize ?? undefined }}
                      className={`px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider relative ${
                        index < headerGroup.headers.length - 1 ? 'border-r border-gray-200' : ''
                      }`}
                    >
                      <div
                        className={`flex w-full min-w-0 items-center justify-center gap-1.5 ${
                          canFilter ? "flex-col" : "flex-row"
                        }`}
                      >
                        <div className="flex items-center justify-center gap-1 min-w-0">
                          {canSort ? (
                            <button
                              type="button"
                              onClick={header.column.getToggleSortingHandler()}
                              className="flex items-center justify-center gap-1 hover:bg-[#FFE5D0] px-1.5 py-1 rounded transition-colors cursor-pointer shrink-0 max-w-full"
                            >
                              <span className="truncate">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              {{
                                asc: <ArrowUp className="w-4 h-4 text-orange-primary shrink-0" />,
                                desc: <ArrowDown className="w-4 h-4 text-orange-primary shrink-0" />,
                              }[header.column.getIsSorted()] ?? (
                                <ArrowUpDown className="w-4 h-4 text-gray-400 shrink-0" />
                              )}
                            </button>
                          ) : (
                            <span className="truncate px-0.5">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                          )}
                        </div>
                        {/* Filter Button */}
                        {canFilter && (
                          <div className="relative filter-menu-container shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenFilterMenu(openFilterMenu === header.column.id ? null : header.column.id);
                              }}
                              className={`p-1 rounded hover:bg-[#FFE5D0] transition-colors relative flex-shrink-0 ${
                                columnFilters[header.column.id] ? 'text-orange-primary' : 'text-gray-400'
                              }`}
                              title={getFilterDisplayValue(header.column.id) || "Filter"}
                            >
                              <Filter className="w-4 h-4" />
                              {columnFilters[header.column.id] && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-orange-primary rounded-full border border-white"></span>
                              )}
                            </button>
                            {/* Filter Badge */}
                            {columnFilters[header.column.id] && getFilterDisplayValue(header.column.id) && openFilterMenu !== header.column.id && (
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-1.5 py-0.5 bg-orange-primary text-white text-[10px] rounded whitespace-nowrap z-40 pointer-events-none shadow-sm">
                                {getFilterDisplayValue(header.column.id)}
                              </div>
                            )}
                            {/* Filter Dropdown */}
                            {openFilterMenu === header.column.id && (
                              <div 
                                className="absolute top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 p-2 w-[160px] max-h-[300px] overflow-y-auto" 
                                onClick={(e) => e.stopPropagation()}
                                style={{ 
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginTop: columnFilters[header.column.id] ? '20px' : '4px'
                                }}
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-xs font-semibold text-gray-700">Filter</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenFilterMenu(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                                <div className="space-y-1.5">
                                  <div>
                                    <label className="text-xs text-gray-600 mb-0.5 block text-left">Min</label>
                                    <input
                                      type="number"
                                      value={columnFilters[header.column.id]?.min ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value === "" ? null : Number(e.target.value);
                                        setColumnFilters(prev => {
                                          const newFilters = { ...prev };
                                          if (value === null && (!newFilters[header.column.id]?.max)) {
                                            delete newFilters[header.column.id];
                                          } else {
                                            newFilters[header.column.id] = {
                                              ...newFilters[header.column.id],
                                              min: value
                                            };
                                          }
                                          return newFilters;
                                        });
                                      }}
                                      className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-primary text-left"
                                      placeholder="Min"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-600 mb-0.5 block text-left">Max</label>
                                    <input
                                      type="number"
                                      value={columnFilters[header.column.id]?.max ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value === "" ? null : Number(e.target.value);
                                        setColumnFilters(prev => {
                                          const newFilters = { ...prev };
                                          if (value === null && (!newFilters[header.column.id]?.min)) {
                                            delete newFilters[header.column.id];
                                          } else {
                                            newFilters[header.column.id] = {
                                              ...newFilters[header.column.id],
                                              max: value
                                            };
                                          }
                                          return newFilters;
                                        });
                                      }}
                                      className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-primary text-left"
                                      placeholder="Max"
                                    />
                                  </div>
                                </div>
                                {(columnFilters[header.column.id]?.min !== undefined || columnFilters[header.column.id]?.max !== undefined) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setColumnFilters(prev => {
                                        const newFilters = { ...prev };
                                        delete newFilters[header.column.id];
                                        return newFilters;
                                      });
                                    }}
                                    className="mt-2 w-full text-xs text-red-600 hover:text-red-800 py-1"
                                  >
                                    Clear Filter
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {enableResizing && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-orange-primary bg-gray-300"
                          style={{ userSelect: 'none' }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableWithFilteredData.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={tableColumns.length} className="px-6 py-12 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              tableWithFilteredData.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  role={onRowClick ? 'button' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                  className={`transition-colors ${
                    onRowClick
                      ? 'hover:bg-[#FFF3EB] cursor-pointer scroll-mt-24 scroll-mb-24'
                      : 'hover:bg-[#FFF3EB]'
                  }`}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const isFirstColumn = index === 0;
                    const cellMinSize = cell.column.columnDef.minSize ?? 100;
                    const cellSize = cell.column.getSize();
                    return (
                      <td
                        key={cell.id}
                        style={{ width: cellSize, minWidth: cellMinSize, maxWidth: cell.column.columnDef.maxSize ?? undefined }}
                        className={`px-3 py-3 ${
                          index < row.getVisibleCells().length - 1 ? 'border-r border-gray-200' : ''
                        } ${isFirstColumn ? 'text-left' : 'text-center'}`}
                      >
                        <div className="min-w-0 max-w-full overflow-hidden">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReusableTable;
